class ApiError extends Error {
  constructor(message, status, detail = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail || message;
  }
}

async function readJson(response) {
  const contentType = response.headers?.get?.('content-type') || 'application/json';
  if (!contentType.includes('application/json')) {
    return null;
  }
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

async function throwApiError(response, fallbackMessage) {
  const payload = await readJson(response);
  const detail = typeof payload?.detail === 'string' && payload.detail ? payload.detail : fallbackMessage;
  throw new ApiError(detail, response.status, detail);
}

async function apiRequest(path, options = {}, fallbackMessage = 'Request failed.') {
  const response = await fetch(path, {
    credentials: 'include',
    ...options,
    headers: {
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    await throwApiError(response, fallbackMessage);
  }
  return response;
}

async function apiGet(path, fallbackMessage) {
  const response = await apiRequest(path, {}, fallbackMessage);
  return response.json();
}

async function apiJson(path, method, payload, fallbackMessage) {
  const response = await apiRequest(
    path,
    {
      method,
      body: JSON.stringify(payload),
    },
    fallbackMessage
  );
  return response.json();
}

export function isAuthError(error) {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

export function buildLoginHref() {
  const onFrontendAuthPath = window.location.pathname.startsWith('/auth/');
  const next = onFrontendAuthPath
    ? `/${window.location.hash || '#/'}`
    : `${window.location.pathname}${window.location.search}${window.location.hash || '#/'}`;
  return `/auth/login?next=${encodeURIComponent(next)}`;
}

export async function fetchAuthMe() {
  return apiGet('/auth/me', 'Failed to load access context.');
}

export async function fetchReleaseMetadata() {
  return apiGet('/release', 'Failed to load release metadata.');
}

export async function switchActiveTenant(payload) {
  return apiJson('/auth/active-tenant', 'POST', payload, 'Failed to switch tenant.');
}

export async function fetchMemberships(tenantId) {
  return apiGet(`/tenants/${tenantId}/memberships`, 'Failed to load memberships.');
}

export async function createAlphaTenant(payload) {
  return apiJson('/alpha/tenants', 'POST', payload, 'Failed to create customer tenant.');
}

export async function createMembership(tenantId, payload) {
  return apiJson(`/tenants/${tenantId}/memberships`, 'POST', payload, 'Failed to add member.');
}

export async function updateMembership(membershipId, payload) {
  return apiJson(`/memberships/${membershipId}`, 'PATCH', payload, 'Failed to update member.');
}

export async function deleteMembership(membershipId) {
  const response = await apiRequest(`/memberships/${membershipId}`, { method: 'DELETE' }, 'Failed to remove member.');
  return response.json();
}

export async function fetchAuditEvents(params = {}) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== '')
  ).toString();
  return apiGet(`/audit-events${query ? `?${query}` : ''}`, 'Failed to load audit events.');
}

export async function logout() {
  const response = await apiRequest('/auth/logout', { method: 'POST' }, 'Failed to sign out.');
  if (response.status === 204) {
    return null;
  }
  return readJson(response);
}

export async function fetchSystems() {
  return apiGet('/systems', 'Failed to load systems.');
}

export async function fetchSystem(systemId) {
  return apiGet(`/systems/${systemId}`, 'Failed to load system.');
}

export async function fetchOverviewData(systemId) {
  return apiGet(`/systems/${systemId}/overview`, 'Failed to load overview.');
}

export async function fetchSourceConnections(systemId) {
  return apiGet(`/systems/${systemId}/source-connections`, 'Failed to load source connections.');
}

export async function fetchExternalLinks(systemId) {
  return apiGet(`/systems/${systemId}/external-links`, 'Failed to load external links.');
}

export async function createExternalLink(systemId, payload) {
  return apiJson(`/systems/${systemId}/external-links`, 'POST', payload, 'Failed to create external link.');
}

export async function updateExternalLink(externalLinkId, payload) {
  return apiJson(`/external-links/${externalLinkId}`, 'PATCH', payload, 'Failed to update external link.');
}

export async function deleteExternalLink(externalLinkId) {
  await apiRequest(`/external-links/${externalLinkId}`, { method: 'DELETE' }, 'Failed to delete external link.');
}

export async function fetchExportDestinations() {
  return apiGet('/export-destinations', 'Failed to load export destinations.');
}

export async function createExportDestination(payload) {
  return apiJson('/export-destinations', 'POST', payload, 'Failed to create export destination.');
}

export async function updateExportDestination(exportDestinationId, payload) {
  return apiJson(`/export-destinations/${exportDestinationId}`, 'PATCH', payload, 'Failed to update export destination.');
}

export async function fetchEvaluationToolConnections() {
  return apiGet('/evaluation-tool-connections', 'Failed to load evaluation tool connections.');
}

export async function createEvaluationToolConnection(payload) {
  return apiJson('/evaluation-tool-connections', 'POST', payload, 'Failed to create evaluation tool connection.');
}

export async function updateEvaluationToolConnection(connectionId, payload) {
  return apiJson(`/evaluation-tool-connections/${connectionId}`, 'PATCH', payload, 'Failed to update evaluation tool connection.');
}

export async function fetchEvaluationToolConnectionSystems(connectionId) {
  return apiGet(`/evaluation-tool-connections/${connectionId}/systems`, 'Failed to load Automated-System-Eval systems.');
}

export async function fetchEvaluationToolMappings(systemId) {
  return apiGet(`/systems/${systemId}/evaluation-tool-mappings`, 'Failed to load evaluation tool mappings.');
}

export async function createEvaluationToolMapping(systemId, payload) {
  return apiJson(`/systems/${systemId}/evaluation-tool-mappings`, 'POST', payload, 'Failed to create evaluation tool mapping.');
}

export async function updateEvaluationToolMapping(mappingId, payload) {
  return apiJson(`/evaluation-tool-mappings/${mappingId}`, 'PATCH', payload, 'Failed to update evaluation tool mapping.');
}

export async function fetchArchitectureData(systemId) {
  const [architecture, findings] = await Promise.all([
    apiGet(`/systems/${systemId}/architecture`, 'Failed to load architecture.'),
    apiGet(`/systems/${systemId}/findings`, 'Failed to load architecture.'),
  ]);
  return { architecture, findings };
}

export async function fetchFindingsListData(systemId, route) {
  const findingsQuery = new URLSearchParams(
    Object.entries({
      severity: route.severity,
      status: route.status,
      prevalence: route.prevalence,
      workflow_id: route.workflowId,
      component_id: route.componentId,
    }).filter(([, value]) => value)
  ).toString();
  const [findings, workflows, components, coverageBoard] = await Promise.all([
    apiGet(`/systems/${systemId}/findings${findingsQuery ? `?${findingsQuery}` : ''}`, 'Failed to load findings.'),
    apiGet(`/systems/${systemId}/workflows`, 'Failed to load findings.'),
    apiGet(`/systems/${systemId}/components`, 'Failed to load findings.'),
    fetchRuntimeRiskCoverageBoard(systemId, {
      workflowId: route.workflowId,
      findingStatus: route.status,
    }),
  ]);
  const coverageRowsByFindingId = new Map(
    (coverageBoard?.groups || [])
      .flatMap((group) => group.rows || [])
      .map((row) => [row.finding_id, row])
  );
  return {
    findings: findings.map((finding) => ({
      ...finding,
      runtime_risk_coverage_row: coverageRowsByFindingId.get(finding.id) || null,
    })),
    workflows,
    components,
  };
}

export async function fetchFindingDetailData(findingId) {
  const detail = await apiGet(`/findings/${findingId}`, 'Failed to load finding detail.');
  return { detail };
}

export async function fetchSystemFindingsData(systemId) {
  return apiGet(`/systems/${systemId}/findings`, 'Failed to load findings.');
}

export async function fetchFindingComparisonData(findingId) {
  return apiGet(`/findings/${findingId}/comparison`, 'Failed to load finding comparison.');
}

export async function fetchRecommendationDetailData(systemId, recommendationId) {
  const [recommendation, findings] = await Promise.all([
    apiGet(`/recommendations/${recommendationId}`, 'Failed to load recommendation detail.'),
    apiGet(`/systems/${systemId}/findings`, 'Failed to load recommendation detail.'),
  ]);
  return { recommendation, findings };
}

export async function fetchWorkflowSummariesData(systemId) {
  return apiGet(`/systems/${systemId}/workflow-summaries`, 'Failed to load workflows.');
}

export async function fetchWorkflowDetailData(workflowId) {
  return apiGet(`/workflows/${workflowId}/detail`, 'Failed to load workflow detail.');
}

export async function fetchRuntimeRiskCoverageBoard(systemId, filters = {}) {
  const query = new URLSearchParams(
    Object.entries({
      workflow_id: filters.workflowId,
      finding_status: filters.findingStatus,
      finding_priority: filters.findingPriority,
      coverage_status: filters.coverageStatus,
      attention_only: filters.attentionOnly ? 'true' : null,
    }).filter(([, value]) => value !== null && value !== undefined && value !== '')
  ).toString();
  return apiGet(
    `/systems/${systemId}/runtime-risk-coverage-board${query ? `?${query}` : ''}`,
    'Failed to load runtime coverage board.'
  );
}

export async function fetchEvalPackDetailData(evalPackId) {
  return apiGet(`/eval-packs/${evalPackId}`, 'Failed to load validation pack.');
}

export async function fetchEvalPackRunDetailData(evalPackRunId) {
  return apiGet(`/eval-pack-runs/${evalPackRunId}`, 'Failed to load validation result run.');
}

export async function fetchRunTraceData(runId) {
  return apiGet(`/runs/${runId}/trace`, 'Failed to load run trace.');
}

export async function downloadEvalPackExport(evalPackId) {
  const payload = await apiGet(`/eval-packs/${evalPackId}/export`, 'Failed to export validation pack.');
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `eval-pack-${evalPackId}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

export async function importEvalPackResults(evalPackId, payload) {
  return apiJson(`/eval-packs/${evalPackId}/result-imports`, 'POST', payload, 'Failed to import validation results.');
}

export async function runEvalPackAutoChecks(evalPackId) {
  const response = await apiRequest(`/eval-packs/${evalPackId}/auto-check-runs`, { method: 'POST' }, 'Failed to run automatic validation checks.');
  return response.json();
}

export function buildRunTraceHref({
  systemId,
  runId,
  primaryStepId = null,
  comparisonStepIds = [],
  sourceFindingId = null,
}) {
  if (!runId) {
    return '#';
  }
  if (!systemId) {
    return `/runs/${runId}/trace`;
  }

  const query = new URLSearchParams();
  if (primaryStepId) {
    query.set('primary_step_id', primaryStepId);
  }
  comparisonStepIds.filter(Boolean).forEach((stepId) => {
    query.append('comparison_step_id', stepId);
  });
  if (sourceFindingId) {
    query.set('source_finding_id', sourceFindingId);
  }

  const queryString = query.toString();
  return `#/systems/${systemId}/runs/${runId}/trace${queryString ? `?${queryString}` : ''}`;
}

export async function fetchFindingEvalPackPreview(findingId) {
  return apiGet(`/findings/${findingId}/eval-pack-preview`, 'Failed to prepare validation preview.');
}

export async function createFindingEvalPack(findingId) {
  const response = await apiRequest(`/findings/${findingId}/eval-packs`, { method: 'POST' }, 'Failed to create validation pack.');
  return response.json();
}

export async function createRuntimeScenarioReview(findingId, payload) {
  return apiJson(`/findings/${findingId}/runtime-scenario-reviews`, 'POST', payload, 'Failed to save scenario review.');
}

export async function fetchRuntimeRiskSuggestionPreview(reviewId) {
  return apiGet(`/runtime-scenario-reviews/${reviewId}/risk-suggestion-preview`, 'Failed to prepare evaluation handoff preview.');
}

export async function fetchRuntimeRiskSuggestionSendStatus(reviewId) {
  return apiGet(`/runtime-scenario-reviews/${reviewId}/evaluation-send-status`, 'Failed to load evaluation send status.');
}

export async function sendRuntimeRiskSuggestion(reviewId, payload = {}) {
  return apiJson(`/runtime-scenario-reviews/${reviewId}/evaluation-sends`, 'POST', payload, 'Failed to send to evaluation coverage.');
}

export async function createRuntimeScenarioEvalPack(reviewId) {
  return apiJson(`/runtime-scenario-reviews/${reviewId}/eval-packs`, 'POST', {}, 'Failed to create validation pack from scenario review.');
}

export async function createFindingExport(findingId, payload) {
  return apiJson(`/findings/${findingId}/exports`, 'POST', payload, 'Failed to export finding.');
}

export async function createImprovementPlanForFinding(findingId, payload) {
  return apiJson(`/findings/${findingId}/improvement-plans`, 'POST', payload, 'Failed to create improvement plan.');
}

export async function updateImprovementPlan(improvementPlanId, payload) {
  return apiJson(`/improvement-plans/${improvementPlanId}`, 'PATCH', payload, 'Failed to update improvement plan.');
}

export async function updateFindingLifecycle(findingId, payload) {
  return apiJson(`/findings/${findingId}`, 'PATCH', payload, 'Failed to update finding lifecycle.');
}

export async function replayIngestion(ingestionId, payload = {}) {
  return apiJson(`/ingestions/${ingestionId}/replay`, 'POST', payload, 'Failed to replay ingestion.');
}

export async function backfillSourceConnection(sourceConnectionId, payload) {
  return apiJson(`/source-connections/${sourceConnectionId}/backfill`, 'POST', payload, 'Failed to request source backfill.');
}

export async function fetchCompareWorkspaceData(systemId) {
  const summary = await apiGet(`/systems/${systemId}/compare/summary`, 'Failed to load compare summary.');
  const [findings, workflows, components] = await Promise.all([
    optionalCompareWorkspaceGet(`/systems/${systemId}/compare/findings`, 'Finding comparisons'),
    optionalCompareWorkspaceGet(`/systems/${systemId}/compare/workflows`, 'Workflow comparisons'),
    optionalCompareWorkspaceGet(`/systems/${systemId}/compare/components`, 'Component comparisons'),
  ]);
  return {
    summary,
    findings: findings.value || [],
    workflows: workflows.value || [],
    components: components.value || [],
    warnings: [findings.warning, workflows.warning, components.warning].filter(Boolean),
  };
}

async function optionalCompareWorkspaceGet(path, label) {
  try {
    return { value: await apiGet(path, `Failed to load ${label.toLowerCase()}.`), warning: null };
  } catch (error) {
    const detail = error?.detail || error?.message || `Failed to load ${label.toLowerCase()}.`;
    return { value: null, warning: `${label} could not load: ${detail}` };
  }
}

export async function fetchOnboardingStatus(systemId) {
  return apiGet(`/systems/${systemId}/onboarding-status`, 'Failed to load onboarding status.');
}

export async function fetchComponentsData(systemId) {
  return apiGet(`/systems/${systemId}/components`, 'Failed to load components.');
}

export async function fetchComponentDetailData(systemId, componentId) {
  const [detail, components] = await Promise.all([
    apiGet(`/components/${componentId}`, 'Failed to load component detail.'),
    apiGet(`/systems/${systemId}/components`, 'Failed to load component detail.'),
  ]);
  return { detail, components };
}

export async function deleteSystem(systemId) {
  await apiRequest(`/systems/${systemId}`, { method: 'DELETE' }, 'Failed to delete system.');
}

export async function onboardSystem(formData) {
  const response = await apiRequest('/systems/onboard', { method: 'POST', body: formData }, 'Failed to onboard system.');
  return response.json();
}

export async function uploadRuntimeImport(systemId, formData) {
  const response = await apiRequest(`/systems/${systemId}/runtime-imports`, { method: 'POST', body: formData }, 'Failed to append runtime data.');
  return response.json();
}
